"use client";
import { useState, useEffect } from 'react'
import { FiTrash2, FiX, FiMessageCircle } from 'react-icons/fi'

import {clearConversationHistory} from "@/lib/api-chatbot-client";
import {useSessionChatbot} from "@/lib/hooks/chatbot/useSessionChatbot";
import {useConversation} from "@/lib/hooks/chatbot/useConversation";
import {useQuestionnaire} from "@/lib/hooks/chatbot/useQuestionnaire";
import {DashboardChatBot} from "@/components/ChatBot/sections/DashboardChatBot";
import {ChatMessages} from "@/components/ChatBot/sections/ChatMessage";
import {MatchModal} from "@/components/ChatBot/sections/MatchModal";
import {CalendarModal} from "@/components/ChatBot/sections/CalendarModal";
import {MessageInput} from "@/components/ChatBot/sections/MessageInput";

const QUESTIONS = [
    { id: 1, text: "Bienvenue! Quel est votre nom?" },
    { id: 2, text: "Quel est votre prénom?" },
    { id: 3, text: "Quel est votre âge?" },
    { id: 4, text: "Quel est votre sexe?" },
    { id: 5, text: "Quelle est votre taille?" },
    { id: 6, text: "Quel est votre poids?" },
    { id: 7, text: "Quel est votre objectif?" }
]

export default function Chatbot() {
    const [isOpen, setIsOpen] = useState(false)
    const [isClearing, setIsClearing] = useState(false)

    // Sessions et messages
    const {
        sessionId,
        userId,
        messages,
        setMessages,
        userInfo,
        setUserInfo,
        currentStep,
        setCurrentStep
    } = useSessionChatbot(QUESTIONS)

    // Conversation et modales
    const {
        input,
        setInput,
        isTyping,
        setIsTyping,
        isPlan,
        setIsPlan,
        isMatch,
        setIsMatch,
        matchResults,
        setMatchResults,
        messagesEndRef,
        handleBotResponse
    } = useConversation(userId, sessionId)

    // Questionnaire
    const { handleQuestionnaireResponse } = useQuestionnaire(QUESTIONS)

    // États modales
    const [eventTitle, setEventTitle] = useState("")
    const [selectedDate, setSelectedDate] = useState("")
    const [dashboardData, setDashboardData] = useState({
        lastMessage: '',
        count: 0
    })

    // Afficher la première question quand le chat s'ouvre
    useEffect(() => {
        if (isOpen && currentStep === 0 && messages.length === 1) {
            const firstQuestion = QUESTIONS[0]
            setMessages(prev => [...prev, {
                id: prev.length + 1,
                text: firstQuestion.text,
                sender: 'bot',
                timestamp: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
            }])
        }
    }, [isOpen])

    // Scroller au bas quand les messages changent
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [messages, messagesEndRef])

    // Gérer l'envoi de message
    const handleSend = async () => {
        if (input.trim() === '') return

        const newMessage = {
            id: messages.length + 1,
            text: input,
            sender: "user",
            timestamp: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
        }

        setMessages([...messages, newMessage])
        setInput("")
        setDashboardData({
            lastMessage: input,
            count: dashboardData.count + 1
        })

        // Questionnaire en cours
        if (currentStep < QUESTIONS.length) {
            await handleQuestionnaireResponse(
                input,
                currentStep,
                userId,
                sessionId,
                userInfo,
                setMessages,
                setUserInfo,
                setCurrentStep,
                setIsTyping
            )
            return
        }

        // Mode normal: réponse du bot
        await handleBotResponse(input, setMessages)
    }

    // Effacer l'historique
    const handleClearHistory = async () => {
        if (!sessionId) {
            alert("Session non disponible")
            return
        }

        if (window.confirm("⚠️ Êtes-vous sûr de vouloir effacer tout l'historique ?")) {
            setIsClearing(true)
            try {
                const result = await clearConversationHistory(sessionId)
                if (result.success) {
                    // Réinitialiser avec la première question
                    const firstQuestion = QUESTIONS[0]
                    setMessages([
                        { id: 1, text: "Bonjour comment puis-je vous aider ?", sender: "bot" },
                        { id: 2, text: firstQuestion.text, sender: "bot", timestamp: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) }
                    ])
                    setCurrentStep(0)
                    setUserInfo({
                        nom: "",
                        prenom: "",
                        age: "",
                        sexe: "",
                        taille: "",
                        poids: "",
                        objectif: ""
                    })
                    setDashboardData({
                        lastMessage: '',
                        count: 0
                    })
                } else {
                    alert("❌ Erreur lors de la suppression")
                }
            } catch (error) {
                console.error("Erreur:", error)
                alert("❌ Erreur de connexion")
            } finally {
                setIsClearing(false)
            }
        }
    }

    return (
        <>
            {/* Bouton chatbot */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? "Fermer le chat" : "Ouvrir le chat"}
                className="fixed bottom-6 right-6 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-primary text-white shadow-lg shadow-primary/40 ring-4 ring-white transition-transform duration-200 hover:scale-105 hover:shadow-xl hover:shadow-primary/50 active:scale-95"
            >
                {isOpen ? (
                    <FiX className="h-7 w-7" />
                ) : (
                    <img src={'/cwf.png'} alt="chatbot" className="h-11 w-11 rounded-full object-cover" />
                )}
            </button>

            {/* Modales */}
            <CalendarModal
                isPlan={isPlan}
                setIsPlan={setIsPlan}
                eventTitle={eventTitle}
                setEventTitle={setEventTitle}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                sessionId={sessionId}
            />

            <MatchModal
                isMatch={isMatch}
                setIsMatch={setIsMatch}
                matchResults={matchResults}
            />

            {/* Chat principal */}
            {isOpen && (
                <div className="fixed bottom-24 right-6 z-50 flex w-[min(92vw,620px)] animate-in fade-in slide-in-from-bottom-4 duration-300 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl sm:flex-row"
                     style={{ height: 'min(70vh, 560px)' }}>
                    {/* Colonne chat */}
                    <div className="flex min-h-0 flex-1 flex-col bg-card">
                        {/* En-tête */}
                        <div className="flex items-center justify-between border-b border-border bg-gradient-to-r from-primary to-[#FFD133] px-4 py-3">
                            <div className="flex items-center gap-2.5">
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/25">
                                    <FiMessageCircle className="h-5 w-5 text-white" />
                                </div>
                                <div className="leading-tight">
                                    <p className="font-poppins text-sm font-semibold text-white">CWF Coach</p>
                                    <p className="flex items-center gap-1 text-[11px] text-white/80">
                                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-cwfGreen" />
                                        En ligne
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={handleClearHistory}
                                    disabled={isClearing}
                                    title="Effacer l'historique"
                                    className="flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isClearing ? (
                                        <span className="text-xs">…</span>
                                    ) : (
                                        <FiTrash2 className="h-4 w-4" />
                                    )}
                                </button>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    title="Fermer"
                                    className="flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/20 hover:text-white"
                                >
                                    <FiX className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        {/* Messages */}
                        <ChatMessages
                            messages={messages}
                            isTyping={isTyping}
                            messagesEndRef={messagesEndRef}
                        />

                        {/* Saisie */}
                        <MessageInput
                            input={input}
                            setInput={setInput}
                            handleSend={handleSend}
                        />
                    </div>

                    {/* Colonne dashboard */}
                    <DashboardChatBot
                        userInfo={userInfo}
                        currentStep={currentStep}
                        questions={QUESTIONS}
                    />
                </div>
            )}
        </>
    )
}